import type { Message, ToolCall } from '@ag-ui/core';

import type { ChatMessage, ChatPart } from '@core/tellent-assistant-types';

export function aguiMessagesToChatMessages(msgs: readonly Message[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  let turn: ChatMessage | null = null;

  for (const m of msgs) {
    if (m.role === 'user') {
      turn = null;
      out.push({
        id: m.id,
        role: 'user',
        parts: [
          {
            kind: 'text',
            messageId: m.id,
            content: userContent(m),
            done: true,
          },
        ],
        createdAt: new Date(),
      });
      continue;
    }

    if (m.role === 'assistant') {
      if (!turn) {
        turn = { id: m.id, role: 'assistant', parts: [], createdAt: new Date() };
        out.push(turn);
      }
      if (typeof m.content === 'string' && m.content.length > 0) {
        turn.parts.push({ kind: 'text', messageId: m.id, content: m.content, done: true });
      }
      if (m.toolCalls) {
        for (const tc of m.toolCalls) {
          turn.parts.push(toolCallPart(tc));
        }
      }
      continue;
    }

    if (m.role === 'tool' && turn) {
      const name = findToolName(turn.parts, m.toolCallId);
      turn.parts.push({
        kind: 'tool-result',
        toolCallId: m.toolCallId,
        name,
        payload: parseJson(m.content),
      });
    }
  }

  return out;
}

export function chatMessagesToAgui(msgs: readonly ChatMessage[]): Message[] {
  const out: Message[] = [];

  for (const m of msgs) {
    if (m.role === 'user') {
      const text = m.parts.find(p => p.kind === 'text');
      out.push(<Message>{
        id: m.id,
        role: 'user',
        content: text && text.kind === 'text' ? text.content : '',
      });
      continue;
    }

    const content = m.parts
      .filter(p => p.kind === 'text')
      .map(p => (p.kind === 'text' ? p.content : ''))
      .join('');
    const toolCalls = m.parts
      .filter(p => p.kind === 'tool-call')
      .map(p => (p.kind === 'tool-call' ? toAguiToolCall(p) : null))
      .filter((x): x is ToolCall => x !== null);

    if (content.length > 0 || toolCalls.length > 0) {
      out.push(<Message>{
        id: m.id,
        role: 'assistant',
        ...(content.length > 0 ? { content } : {}),
        ...(toolCalls.length > 0 ? { toolCalls } : {}),
      });
    }

    for (const p of m.parts) {
      if (p.kind !== 'tool-result') continue;
      out.push(<Message>{
        id: crypto.randomUUID(),
        role: 'tool',
        toolCallId: p.toolCallId,
        content: typeof p.payload === 'string' ? p.payload : JSON.stringify(p.payload ?? ''),
      });
    }
  }

  return out;
}

function userContent(m: Extract<Message, { role: 'user' }>): string {
  if (typeof m.content === 'string') return m.content;
  return '';
}

function toolCallPart(tc: ToolCall): ChatPart {
  return {
    kind: 'tool-call',
    toolCallId: tc.id,
    name: tc.function.name,
    args: parseJson(tc.function.arguments),
    status: 'done',
    startedAt: 0,
  };
}

function toAguiToolCall(p: Extract<ChatPart, { kind: 'tool-call' }>): ToolCall {
  return {
    id: p.toolCallId,
    type: 'function',
    function: {
      name: p.name,
      arguments: typeof p.args === 'string' ? p.args : JSON.stringify(p.args ?? {}),
    },
  };
}

function findToolName(parts: readonly ChatPart[], toolCallId: string): string {
  for (const p of parts) {
    if (p.kind === 'tool-call' && p.toolCallId === toolCallId) return p.name;
  }
  return 'unknown';
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}
